import {
    ENROLLMENT_BEGIN,
    ENROLLMENT_CANCEL,
    ENROLLMENT_COMPLETE,
    ENROLLMENT_DEVICESERVER_ERROR,
    ENROLLMENT_RESET,
} from "../actions/enrollment";
import {
    DEVICE_AUTH_FAIL,
    DEVICE_AUTH_SUCCESS,
    DEVICE_CAPTURE,
    DEVICE_STATUS,
} from "../../../site-timeclock/actions/device";

import * as Actions from "../actions";

import { Errors, Messages, EnrollmentMessages } from "../messages";
import { AuthenticatedPerson, DeviceResult, StampDirection } from "../models";
import { Action } from "../models/redux";
import { TypedUseSelectorHook, useSelector } from "react-redux";
import { MessageDescriptor } from "react-intl";

export enum View {
    Entry = 1,
    Stamp = 2,
    Enrollment = 3,
    Settings = 4,
    MultiStamp = 5,
    SelfRegistration = 6,
    Balance = 7,
}

export interface JsonError {
    type: string;
    values: unknown;
}

export type SystemMessage = MessageDescriptor & { values?: Record<string, unknown>, type?: string };

export interface Message {
    userMessage?: string;
    userMessageHeader?: string;
    systemMessage?: SystemMessage;
    device?: boolean;
    noButton?: boolean;
    jsonError?: JsonError;
}

export interface Session {
    view?: View;

    messages: Message[];

    loginTimerId?: number;
    apiLoading?: boolean;
    apiOfflineApp?: boolean;

    deviceSupport?: Array<{ key: string; value: string }>;
    deviceResults?: DeviceResult[];
    personRef?: number;

    // registration request, fingerRef
    enrollmentMeta?: string;
    enrollmentToScan?: number;

    // stamp operations
    stampPerson?: AuthenticatedPerson;
    stampTimerId?: number;
    lastReloadTime?: Date;

    stampDirection: StampDirection;

    geoposition?: GeolocationPosition | GeolocationPositionError;
}

export function session(state: Session, action: Action): Session {
    if (state === undefined) {
        return { view: null, messages: [], stampDirection: StampDirection.None };
    }

    switch (action.type) {
        case Actions.CANCEL:
            return { ...state, view: View.Entry, stampPerson: null, stampDirection: StampDirection.None };
        case Actions.BACK_STAMP:
            return { ...state, view: View.Stamp, stampPerson: state.stampPerson };
        case Actions.API_LOAD:
            return { ...state, apiLoading: true, apiOfflineApp: false };
        case Actions.API_ERROR: {
            const systemMessage = action.message == undefined ? action.systemMessage : action.message.jsonError as SystemMessage;
            
            const messages = state.view === View.Settings ? [] : [{ systemMessage }, ...state.messages];

            return { ...state, messages, apiLoading: false, loginTimerId: action.loginTimerId };
        }
        case Actions.API_OFFLINE:
            return { ...state, view: state.view || View.Entry, loginTimerId: action.loginTimerId, apiLoading: false };
        case Actions.API_OFFLINE_APP:
            return { ...state, apiOfflineApp: true };
        case Actions.SESSION_INIT:
            return { ...state, view: View.Entry, messages: [], loginTimerId: null };
        case DEVICE_CAPTURE: {
            const catchCapture =
                state.view === View.Entry ||
                state.view === View.Enrollment ||
                (state.view === View.Stamp && state.stampPerson.demandFingerprint);

            if (catchCapture) {
                const deviceResults = state.deviceResults ? [action.result, ...state.deviceResults] : [action.result];
                return { ...state, messages: [], deviceResults };
            } else {
                // ignore captures in all other views
                return state;
            }
        }
        case DEVICE_STATUS:
            return { ...state, deviceSupport: action.supportedDevices };
        case DEVICE_AUTH_SUCCESS:
            return { ...state, deviceResults: [], personRef: action.personRef };
        case DEVICE_AUTH_FAIL:
            return {
                ...state,
                deviceResults: [],
                messages: [{ userMessage: action.error.message, device: true }, ...state.messages],
            };
        case Actions.MESSAGE_SHOW:
            return { ...state, messages: [action.message, ...state.messages] };
        case Actions.MESSAGE_DISMISS:
            return { ...state, messages: state.messages.slice(1) };

        case ENROLLMENT_BEGIN:
            if (!state.deviceSupport) {
                return { ...state, messages: [{ systemMessage: Errors.NoDevice }], apiLoading: false };
            } else {
                return {
                    ...state,
                    apiLoading: false,
                    enrollmentMeta: action.meta,
                    enrollmentToScan: action.count,
                    personRef: action.personRef,
                    view: View.Enrollment,
                };
            }
        case ENROLLMENT_CANCEL:
            return {
                ...state,
                view: View.Entry,
                deviceResults: [],
            };
        case ENROLLMENT_DEVICESERVER_ERROR:
            return {
                ...state,
                messages: [{ systemMessage: EnrollmentMessages.DeviceServerError }, ...state.messages],
                deviceResults: [],
            };
        case ENROLLMENT_RESET:
            return { ...state, deviceResults: [] };
        case ENROLLMENT_COMPLETE:
            return {
                ...state,
                deviceResults: [],
                messages: [{ systemMessage: EnrollmentMessages.Complete }, ...state.messages],
                view: View.Entry,
            };

        case Actions.STAMP_BEGIN:
            return {
                ...state,
                apiLoading: false,
                view: View.Stamp,
                stampPerson: action.stampPerson,
                stampTimerId: action.stampTimerId,
                lastReloadTime: new Date(),
                messages: [...state.messages, ...action.messages],
            };
        case Actions.STAMP_FINGERPRINT_READ:
            return { ...state, deviceResults: [], stampPerson: { ...state.stampPerson, demandFingerprint: false } };
        case Actions.STAMP_POST:
            return { ...state, apiLoading: true };
        case Actions.STAMP_CONFIRMED:
            return {
                ...state,
                apiLoading: false,
                messages: [
                    {
                        noButton: true,
                        systemMessage: state.loginTimerId ? Messages.StampRegisteredOffline : Messages.StampRegistered,
                    },
                    ...state.messages,
                ],
                view: View.Entry,
                stampDirection: StampDirection.None,
            };

        case Actions.SELF_REGISTRATION_BEGIN:
            return { ...state, view: View.SelfRegistration };

        case Actions.SETTINGS_OPEN:
            return { ...state, view: View.Settings, /*loginTimerId: null, */ messages: [] };

        case Actions.BALANCE_OPEN:
            return {
                ...state,
                view: View.Balance,
                personRef: action.personRef,
            };

        case Actions.GEOPOSITION_UPDATE:
            return { ...state, geoposition: action.position };
        case Actions.GEOPOSITION_ERROR:
            return { ...state, geoposition: action.error };

        case Actions.SET_DIRECTION:
            return { ...state, stampDirection: action.payload };

        default:
            return state;
    }
}

export const useSessionSelector: TypedUseSelectorHook<Session> = (selector) =>
    useSelector((state: { session: Session }) => selector(state.session));
